/**
 * Who a comment may @mention (backend #128 checks the same set): the brand's
 * team and its guests (the client's people). There is no endpoint that lists
 * them for a post, so the picker reads the workspace's member list, which
 * carries each guest's brands. Someone the backend would refuse never shows.
 */
import { useMemo } from "react";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { useOrganizationMembers } from "../../settings/api/useOrganization";
import { AGENCY_NAME } from "./commentModel";

export interface MentionPerson {
  kind: "user";
  id: string;
  name: string;
  /** The line beside the name: the client's name, or the agency. */
  org: string;
}

export function useMentionPeople(clientName: string | null, enabled: boolean): MentionPerson[] {
  const organizationId = useWorkspaceStore((s) => s.selectedOrganizationId);
  const brandId = useWorkspaceStore((s) => s.selectedBrandId);
  const members = useOrganizationMembers(enabled ? organizationId : null);
  return useMemo(() => {
    const client = clientName?.trim() || "Client";
    const people: MentionPerson[] = [];
    for (const member of members.data ?? []) {
      const name = member.name?.trim();
      if (!name) continue;
      if (member.role === "Guest") {
        if (!brandId || !member.brands?.some((brand) => brand.id === brandId)) continue;
        people.push({ kind: "user", id: member.userId, name, org: client });
      } else {
        people.push({ kind: "user", id: member.userId, name, org: AGENCY_NAME });
      }
    }
    // The client's people first: they are who a comment is usually for.
    return people.sort((a, b) => Number(a.org === AGENCY_NAME) - Number(b.org === AGENCY_NAME) || a.name.localeCompare(b.name));
  }, [members.data, brandId, clientName]);
}
